import { useEffect, useId, useRef, useState } from 'react'
import Button from '../Button'
import Input from '../Input'
import type { DialogRequest } from './dialog-types'

interface AppDialogProps {
  request: DialogRequest
  onCancel: () => void
  onSubmit: (value?: string) => void
}

const focusableSelector =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function AppDialog({ request, onCancel, onSubmit }: AppDialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const fieldId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const primaryRef = useRef<HTMLButtonElement>(null)
  const fieldRef = useRef<HTMLInputElement | HTMLSelectElement>(null)
  const [value, setValue] = useState(
    request.kind === 'prompt' || request.kind === 'select' ? (request.options.value ?? '') : ''
  )
  const [error, setError] = useState<string | null>(null)
  const cancellable = request.kind !== 'alert'
  const { options } = request

  useEffect(() => {
    const target = fieldRef.current ?? primaryRef.current
    target?.focus()
  }, [])

  const submit = () => {
    if (request.kind === 'prompt') {
      const validationError = request.options.validate?.(value) ?? null
      if (validationError) {
        setError(validationError)
        return
      }
      onSubmit(value)
      return
    }
    if (request.kind === 'select') {
      onSubmit(value)
      return
    }
    onSubmit()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && cancellable) {
      event.preventDefault()
      onCancel()
      return
    }
    if (event.key === 'Enter' && request.kind === 'prompt') {
      event.preventDefault()
      submit()
      return
    }
    if (event.key !== 'Tab' || !panelRef.current) return
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(focusableSelector))
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first?.focus()
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close dialog"
        className="absolute inset-0 cursor-default bg-slate-950/75 backdrop-blur-sm"
        onClick={cancellable ? onCancel : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={options.description ? descriptionId : undefined}
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-harbor-border bg-harbor-surface shadow-2xl shadow-black/40 motion-safe:animate-[dialog-in_160ms_ease-out]"
      >
        <div className="border-b border-harbor-border px-6 py-5">
          <h2 id={titleId} className="text-lg font-semibold tracking-tight text-harbor-text">
            {options.title}
          </h2>
          {options.description && (
            <p id={descriptionId} className="mt-2 text-sm leading-6 text-harbor-text-secondary">
              {options.description}
            </p>
          )}
        </div>

        {(request.kind === 'prompt' || request.kind === 'select') && (
          <div className="px-6 py-5">
            {request.kind === 'prompt' ? (
              <Input
                ref={fieldRef as React.Ref<HTMLInputElement>}
                id={fieldId}
                label={request.options.label}
                value={value}
                placeholder={request.options.placeholder}
                error={error ?? undefined}
                onChange={(event) => {
                  setValue(event.target.value)
                  if (error) setError(null)
                }}
              />
            ) : (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-harbor-text-secondary">
                  {request.options.label}
                  <select
                    ref={fieldRef as React.Ref<HTMLSelectElement>}
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-harbor-text focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {request.options.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 bg-harbor-surface-raised/40 px-6 py-4">
          {cancellable && (
            <Button variant="secondary" onClick={onCancel}>
              {options.cancelLabel ?? 'Cancel'}
            </Button>
          )}
          <Button
            ref={primaryRef}
            variant={options.tone === 'danger' ? 'danger' : 'primary'}
            onClick={submit}
          >
            {options.confirmLabel ?? (request.kind === 'alert' ? 'Close' : 'Continue')}
          </Button>
        </div>
      </div>
    </div>
  )
}
